import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  People,
  ChevronRight,
  Refresh,
  DeleteOutline,
} from '@mui/icons-material';
import { viewsAPI } from '../services/api';
import { ProspectViewSummary } from '../types';

interface ViewsListPageProps {
  onBack: () => void;
  onOpenView: (viewId: number) => void;
}

const ViewsListPage: React.FC<ViewsListPageProps> = ({ onBack, onOpenView }) => {
  const [views, setViews] = useState<ProspectViewSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingViewId, setDeletingViewId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadViews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await viewsAPI.listViews();
      setViews(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load views');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadViews(); }, [loadViews]);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleDeleteConfirm = async () => {
    if (deletingViewId === null) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await viewsAPI.deleteView(deletingViewId);
      setDeletingViewId(null);
      await loadViews();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete view');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#E5E7EB' }}>

      {/* ── Page header ── */}
      <Box sx={{
        p: 3,
        backgroundColor: '#283E56',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={onBack} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
          <Visibility sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              My Views
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Prospect lists created from batch screening
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadViews} sx={{ color: 'white' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#283E56' }} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        )}

        {!loading && !error && views.length === 0 && (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '2px dashed #CBD5E0' }}>
            <Visibility sx={{ fontSize: 48, color: '#CBD5E0', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6B7280', fontWeight: 600 }}>No views yet</Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 1 }}>
              Run a batch screening and select prospects to create your first view.
            </Typography>
            <Button
              variant="contained"
              onClick={onBack}
              sx={{ mt: 3, backgroundColor: '#283E56', '&:hover': { backgroundColor: '#1C2433' } }}
            >
              Go to Batch Search
            </Button>
          </Paper>
        )}

        {!loading && !error && views.length > 0 && (
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{
              px: 2.5, py: 2,
              backgroundColor: '#283E56',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {views.length} view{views.length !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Click a row to open the view
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#283E56' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#283E56' }}>Prospects</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#283E56', whiteSpace: 'nowrap' }}>Created</TableCell>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {views.map(v => (
                    <TableRow
                      key={v.id}
                      onClick={() => onOpenView(v.id)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(40,62,86,0.04)' },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Visibility sx={{ fontSize: 16, color: '#283E56', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#283E56' }}>
                            {v.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<People sx={{ fontSize: '14px !important' }} />}
                          label={`${v.member_count} prospect${v.member_count !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{ backgroundColor: '#EEF2F7', color: '#283E56', fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#6B7280', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {formatDate(v.created_at)}
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()} sx={{ px: 0.5 }}>
                        <Tooltip title="Delete view">
                          <IconButton
                            size="small"
                            onClick={e => { e.stopPropagation(); setDeletingViewId(v.id); setDeleteError(null); }}
                            sx={{ color: '#CBD5E0', '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.08)' } }}
                          >
                            <DeleteOutline sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <ChevronRight sx={{ color: '#CBD5E0', fontSize: 20 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Box>

      {/* ── Delete confirmation dialog ── */}
      <Dialog
        open={deletingViewId !== null}
        onClose={() => !deleteLoading && setDeletingViewId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#283E56' }}>Delete view?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {views.find(v => v.id === deletingViewId)?.name
              ? <>Are you sure you want to delete <strong>{views.find(v => v.id === deletingViewId)?.name}</strong>? This action cannot be undone.</>
              : 'Are you sure you want to delete this view? This action cannot be undone.'}
          </DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 1.5 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeletingViewId(null)}
            disabled={deleteLoading}
            variant="outlined"
            sx={{ borderColor: '#CBD5E0', color: '#283E56' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            variant="contained"
            color="error"
            sx={{ boxShadow: 'none' }}
          >
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewsListPage;
